<script setup lang="ts">
  import { useModal } from 'vue-final-modal';
  import { Loader2 } from 'lucide-vue-next';
  import EyeIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import PaperclipIcon from '@/shared/assets/icons/PaperclipIcon.svg';
  import TrashIcon from '@/shared/assets/icons/RedTrashIcon.svg';
  import { ConfirmDeleteModal, IconButton, Tooltip } from '@/shared/ui';
  import type { RecordAttachmentRow } from '../model/useRecordAttachments';

  const props = defineProps<{
    row: RecordAttachmentRow;
  }>();

  const emit = defineEmits<{
    open: [key: string];
    delete: [key: string];
  }>();

  const { open: openConfirm, close: closeConfirm } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: 'Tem certeza que deseja deletar o anexo?',
      description: 'A ação não pode ser desfeita.',
      onClose: () => {
        closeConfirm();
      },
      onConfirm: () => {
        closeConfirm();
        emit('delete', props.row.key);
      },
    },
  });
</script>

<template>
  <li
    class="flex items-center justify-between gap-6 border-b border-gray-02 px-6 py-3 last:border-b-0"
    :data-testid="`record-attachment-row-${row.key}`"
  >
    <div class="flex min-w-0 items-center gap-6">
      <span class="flex size-6 shrink-0 items-center justify-center text-gray-07">
        <PaperclipIcon
          aria-hidden="true"
          class="h-4 w-2.5"
          data-testid="record-attachment-icon"
        />
      </span>
      <span
        class="truncate text-sm/8 text-gray-07"
        :title="row.fileName"
        data-testid="record-attachment-name"
      >
        {{ row.fileName }}
      </span>
    </div>

    <span
      v-if="row.sending"
      class="inline-flex shrink-0 text-gray-05"
      role="status"
      data-testid="record-attachment-sending"
    >
      <Loader2 class="h-6 w-6 animate-spin" />
      <span class="sr-only">Enviando</span>
    </span>
    <div
      v-else
      class="flex shrink-0 items-center gap-2 text-gray-xx-02"
    >
      <Tooltip
        content="Visualizar"
        placement="top"
        class="inline-flex"
      >
        <IconButton
          :src="EyeIcon"
          alt="Visualizar"
          class="h-6 w-6"
          data-testid="record-attachment-view"
          @click="emit('open', row.key)"
        />
      </Tooltip>
      <Tooltip
        v-if="row.canDelete"
        content="Deletar"
        placement="top"
        class="inline-flex"
      >
        <IconButton
          :src="TrashIcon"
          alt="Deletar"
          class="h-6 w-6"
          :loading="row.deleting"
          data-testid="record-attachment-delete"
          @click="openConfirm()"
        />
      </Tooltip>
    </div>
  </li>
</template>
